import { isPbsEnabled, Site, SiteEnv, SiteProduct, siteProductOf, SITE_ENVS } from '@pubstack/common/src/site'
import { UpgradeOffer } from '@pubstack/common/src/upgrade'
import { SUBSCRIBE_TO_NODE, SUBSCRIBE_TO_ONE } from '~/modules/pbs/integrations/common/seatSetupOption'

// Re-exported rather than redefined: the bidder mapping and the media list's own product filter ask the
// same questions of a media from `common`, and "what does this media run on?" must not have two answers.
export { isPbsEnabled, siteProductOf }
export type { SiteProduct }

export const SITE_PRODUCT_LABEL: Record<SiteProduct, string> = {
  adm: 'One',
  pbs: 'Node',
  analytics: 'Analytics',
}

export const SITE_PRODUCT_DESCRIPTION: Record<SiteProduct, string> = {
  adm: 'Manage and deploy this media’s header bidding setup with Pubstack.',
  pbs: 'Run this media’s auctions server-side on Pubstack’s Prebid Server.',
  analytics: 'Measure this media’s performance only. Nothing is deployed on it.',
}

/** Ad management needs `adManagementEnabled` and `admStatus` written together — one alone leaves the media half-activated. */
export const siteProductPayload = (product: SiteProduct): Partial<Site> =>
  product === 'adm' ? { adManagementEnabled: true, admStatus: 'to-configure' } : product === 'pbs' ? { pbsStandaloneStatus: 'to-configure' } : {}

export const PRODUCT_IS_FINAL = 'A media runs on a single product. This cannot be changed later without the help of Pubstack’s support.'
export const PRODUCT_IS_FINAL_TITLE = 'This choice is final'
/**
 * Only the edit flyout asks for this. Creating a media is warned about but not typed back: there is no
 * existing media whose identity has to be proved, and the subscription was already picked and reviewed.
 */

/**
 * Whether this save is the one-way step that puts a media on a product.
 *
 * `undefined` is the media being created, where nothing has been picked yet. Analytics is not a product
 * being left behind so much as the absence of one, which is why it is the only state that can still move
 * — and why picking Analytics is never an activation, whichever side it is picked from.
 */
export const isActivatingProduct = (currentProduct: SiteProduct | undefined, selectedProduct: SiteProduct): boolean =>
  (currentProduct ?? 'analytics') === 'analytics' && selectedProduct !== 'analytics'
export const PRODUCT_IS_SET = 'Contact Pubstack’s support to change the product of this media.'
export const ONLY_OWNERS_CAN_ENABLE_PRODUCT = 'Only scope owners can enable a product on a media.'

/** Web is the only environment One and Analytics run on. Everywhere else the product is Node. */
export const ONE_IS_WEB_ONLY = 'One runs on web medias only.'
/** Pubstack measures the web on its own; in-app and AMP inventory is only ever seen through Node. */
export const ANALYTICS_IS_WEB_ONLY = 'Analytics alone runs on web medias. In-app and AMP medias run on Node.'

export type SiteProductAvailability = {
  /**
   * What the media runs on today, or `undefined` while it is being created and nothing has been picked
   * yet. The two are different questions: a media that already runs on Analytics keeps it whatever its
   * environment, while one being created has to pick something its environment actually supports.
   */
  currentProduct?: SiteProduct
  /** The media's environment. Fixed at creation, so on edit this is simply what it was created with. */
  env: SiteEnv
  scopeHasOne: boolean
  scopeHasNode: boolean
  canEnableProduct: boolean
}

/**
 * Why a product cannot be picked for a media, or `undefined` when it can.
 *
 * The environment is never restricted; the product is what narrows. Web takes all three, and off it
 * only Node remains: One is client-side Prebid and runs on web alone — the same reason
 * `SITE_AD_UNIT_ENV_COMPATIBILITY` gives web the only envs carrying adm ad units — and Analytics
 * measures the web on its own but reads in-app and AMP inventory through Prebid Server.
 *
 * Order matters. What the account lacks is said before what the viewer lacks: a non-owner on a scope
 * without Node should learn what Node is, not that they need a role for something nobody bought.
 */
export const siteProductDisabledReason = (product: SiteProduct, { currentProduct, env, scopeHasOne, scopeHasNode, canEnableProduct }: SiteProductAvailability): string | undefined => {
  // Staying on what the media already runs on is not a change, so it is never refused — including an
  // Analytics media on an environment Analytics would no longer be offered for.
  if (currentProduct && product === currentProduct) {
    return undefined
  }
  // Activating a product is one-way, so a media that has one is done choosing.
  if (currentProduct && currentProduct !== 'analytics') {
    return PRODUCT_IS_SET
  }
  // Analytics is the absence of a product: nothing is deployed and nothing is activated, so ownership
  // never comes into it. The environment does, and it is the only thing that ever closes Analytics.
  if (product === 'analytics') {
    return env === 'web' ? undefined : ANALYTICS_IS_WEB_ONLY
  }
  if (product === 'adm') {
    // The environment first: it is a rule no purchase lifts, so pitching One to an in-app media would
    // sell an upgrade that still would not let it run One.
    if (env !== 'web') {
      return ONE_IS_WEB_ONLY
    }
    if (!scopeHasOne) {
      return SUBSCRIBE_TO_ONE
    }
  }
  if (product === 'pbs' && !scopeHasNode) {
    return SUBSCRIBE_TO_NODE
  }
  if (!canEnableProduct) {
    return ONLY_OWNERS_CAN_ENABLE_PRODUCT
  }
  return undefined
}

/**
 * The products a media on this env may be given, in the order they are offered.
 *
 * Empty is a real answer: in-app and AMP medias run on Node alone, so a scope without Node has nothing
 * to pick there. The flyout keeps every card on screen with its reason — that is the cross-sell — and
 * refuses to create rather than quietly falling back to a product the environment does not support.
 */
export const availableSiteProducts = (availability: SiteProductAvailability): SiteProduct[] => SITE_PRODUCT_CHOICES.filter((product) => !siteProductDisabledReason(product, availability))

/**
 * The offers a closed product card pitches, or nothing where the reason is not something to ask for.
 *
 * Only the two subscribe reasons name a product the scope could go and get. "One is web only", "this
 * media already has a product" and the rest are facts about this media, and no upgrade changes them —
 * a crown on those would sell something the buyer would not receive.
 */
export const siteProductUpgradeOffers = (reason: string | undefined): UpgradeOffer[] | undefined => (reason === SUBSCRIBE_TO_ONE ? ['one'] : reason === SUBSCRIBE_TO_NODE ? ['node'] : undefined)

/** Analytics first: it is where every media starts. */
export const SITE_PRODUCT_CHOICES = ['analytics', 'adm', 'pbs'] as const

/**
 * Why a media on this product cannot be given this environment, or `undefined` when it can.
 *
 * The mirror of `siteProductDisabledReason`, for the flyout that asks the product first and then lets
 * each media name its own environment. Deliberately not derived from that function: it answers with
 * everything that can close a product — an offer the scope never bought, a role the viewer lacks — and
 * none of those are the environment's doing. Only the pair is at stake here, and the pair is the two
 * web-only rules, so this says those and nothing else.
 *
 * Node is the only product with a choice to make: One is client-side Prebid and Analytics measures the
 * web on its own, so on either of them every media is a web media.
 */
export const siteEnvDisabledReason = (env: SiteEnv, product: SiteProduct): string | undefined => {
  if (env === 'web') {
    return undefined
  }
  return product === 'adm' ? ONE_IS_WEB_ONLY : product === 'analytics' ? ANALYTICS_IS_WEB_ONLY : undefined
}

/** The environments a media on this product may be created on, in the order they are offered. */
export const availableSiteEnvs = (product: SiteProduct): SiteEnv[] => SITE_ENVS.filter((env) => !siteEnvDisabledReason(env, product))
